import { Ico } from "../../../components/icons/icons.jsx";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { sidebarPrimaryButton } from "../../../components/ui/buttonStyles.js";
import { SearchField } from "../../../components/ui/SearchField.jsx";
import { CreateListEmptyState } from "../../../components/emptyStates/CreateListEmptyState.jsx";
import { SidebarEmptyHint } from "../../../components/emptyStates/SidebarEmptyHint.jsx";
import { DeviceListSkeleton } from "../../../components/skeletons/DeviceListSkeleton.jsx";

export function DeviceSidebar({
  devices,
  selectedId,
  onSelect,
  search,
  onSearchChange,
  onCreate,
  onAssignExisting,
  loading,
  getDeviceTypeName,
}) {
  const { c, dark, accentHex: A } = useThemeSlice();

  return (
    <aside style={{ width: 280, flexShrink: 0, display: "flex", flexDirection: "column", borderRight: `1px solid ${c.cardBdr}`, background: c.sb, minHeight: 0 }}>
      <div style={{ padding: "12px 14px 10px", flexShrink: 0, borderBottom: `1px solid ${c.cardBdr}` }}>
        <SearchField value={search} onChange={onSearchChange} placeholder="Search devices…" ariaLabel="Search devices" variant="sidebar" />
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "2px 10px 10px", minHeight: 0 }}>
        {loading ? (
          <DeviceListSkeleton />
        ) : devices.length === 0 ? (
          <SidebarEmptyHint c={c}>{search.trim() ? "No matching devices" : "No devices yet"}</SidebarEmptyHint>
        ) : (
          devices.map((device) => {
            const active = device.id === selectedId;
            const typeName = getDeviceTypeName?.(device.deviceTypeId) ?? device.deviceType ?? "—";
            return (
              <button
                key={device.id}
                type="button"
                onClick={() => onSelect(device.id)}
                style={{
                  width: "100%",
                  display: "block",
                  textAlign: "left",
                  padding: "10px 12px",
                  marginBottom: 6,
                  borderRadius: 8,
                  border: `1px solid ${active ? `${A}55` : c.cardBdr}`,
                  borderLeft: active ? `3px solid ${A}` : `1px solid ${c.cardBdr}`,
                  background: active ? (dark ? `${A}10` : `${A}06`) : c.hover,
                  color: c.text,
                  cursor: "pointer",
                  boxShadow: active && !dark ? "0 1px 2px rgba(0,0,0,.04)" : "none",
                }}
              >
                <div style={{ fontSize: 11.5, fontWeight: active ? 700 : 600, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", lineHeight: 1.35, wordBreak: "break-all" }}>{device.deviceId}</div>
                <div style={{ fontSize: 11, color: c.sub, marginTop: 3 }}>{typeName}</div>
              </button>
            );
          })
        )}
      </div>

      <div style={{ padding: "12px 14px 14px", flexShrink: 0, borderTop: `1px solid ${c.divider}`, background: c.sb, display: "flex", flexDirection: "column", gap: 8 }}>
        {onAssignExisting && (
          <button type="button" onClick={onAssignExisting} style={{ ...sidebarPrimaryButton(A, dark), background: c.card, color: c.text, border: `1px solid ${c.cardBdr}`, boxShadow: "none" }}>
            {Ico.device}
            Assign existing device
          </button>
        )}
        <button type="button" onClick={onCreate} style={sidebarPrimaryButton(A, dark)}>
          {Ico.plus}
          Create device
        </button>
      </div>
    </aside>
  );
}

export function DevicesEmptyState({ onCreate, hint, title = "No devices yet" }) {
  const { c, dark, accentHex: A } = useThemeSlice();

  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 0, padding: 24, background: c.bg }}>
      <CreateListEmptyState title={title} hint={hint} primaryAction={{ label: "Create device", onClick: onCreate }} c={c} dark={dark} A={A} />
    </div>
  );
}
